'use client';

/**
 * RFQDetails - RFQ entity detail view
 *
 * @deprecated Use EntityDetailsLoader with DetailsPanel instead.
 * This component is kept for fallback during migration.
 * Remove after schema-driven rendering is verified.
 *
 * Displays RFQ information when an RFQ node is selected.
 * Fetches project data to find the specific RFQ, plus vendor contacts.
 *
 * @component_role RFQ Detail View
 * @business_rule Shows RFQ status, buyer/vendor info, line items, and vendor
 *   responses. Fetches vendor contacts for quick messaging access.
 * @user_outcome Quick RFQ status check - see who's responding, what items
 *   are quoted, and who to follow up with.
 * @integration_point klyde_edge /neo/graph/explore/project, /neo/graph/vendor/contacts
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { cn } from '@/lib/utils/cn';
import { Factory, User, ExternalLink } from 'lucide-react';
import { useProjectExplore, useVendorContacts } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';

// Shared components
import {
  StatusBadge,
  SectionHeader,
  VendorContactsList,
  LoadingState,
  LineItemsTable,
  OdooLink,
} from './shared';

// ============================================================
// Configuration
// ============================================================

const VENDOR_RESPONSE_STATUS_CONFIG: Record<string, { color: string }> = {
  quoted: { color: 'bg-success/20 text-success' },
  pending: { color: 'bg-muted text-muted-foreground' },
  declined: { color: 'bg-destructive/20 text-destructive' },
};

// ============================================================
// Sub-Components
// ============================================================

interface VendorResponseCardProps {
  response: {
    cdk?: string | null;
    vendor?: { name: string } | null;
    status?: string | null;
    line_items: unknown[];
  };
  index: number;
}

function VendorResponseCard({ response, index }: VendorResponseCardProps) {
  const statusConfig = VENDOR_RESPONSE_STATUS_CONFIG[response.status || 'pending'] ||
    VENDOR_RESPONSE_STATUS_CONFIG.pending;

  return (
    <div className="p-2 rounded bg-card border border-border">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{response.vendor?.name || 'Unknown Vendor'}</span>
        <span className={cn('text-xs px-1.5 py-0.5 rounded', statusConfig.color)}>
          {response.status || 'pending'}
        </span>
      </div>
      {response.line_items.length > 0 && (
        <div className="mt-2 text-xs text-muted-foreground">
          {response.line_items.length} line items
        </div>
      )}
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

interface RFQDetailsProps {
  nodeId: string;
  projectId?: string | null;
}

export function RFQDetails({ nodeId, projectId }: RFQDetailsProps) {
  const projectExploreStrategy = useCapabilityStore((s) => s.strategyName('project_intelligence', 'explore'));
  const vendorContactsStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_contacts'));

  // Fetch project data to find the specific RFQ
  const { data: projectData, isLoading: projectLoading } = useProjectExplore(
    projectExploreStrategy!,
    projectId
      ? {
          project_id: projectId,
          include_relationships: ['rfqs'],
          max_items: 50,
        }
      : null
  );

  // Find the specific RFQ from project data
  const rfq = projectData?.project?.rfqs.find((r) => r.semantic_id === nodeId);

  // Fetch vendor contacts if RFQ has a vendor
  const { data: vendorContactsData, isLoading: contactsLoading } = useVendorContacts(
    vendorContactsStrategy!,
    rfq?.vendor?.semantic_id ? { vendor_id: rfq.vendor.semantic_id } : null
  );
  const vendorContacts = vendorContactsData?.contacts || [];

  // Loading state
  if (projectLoading) {
    return <LoadingState message="Loading RFQ..." />;
  }

  // No project context
  if (!projectId) {
    return (
      <div className="space-y-4">
        <div>
          <SectionHeader title="SEMANTIC ID" />
          <code className="text-xs bg-muted px-2 py-1 rounded block overflow-x-auto">
            {nodeId}
          </code>
        </div>
        <div className="text-sm text-muted-foreground italic">
          Select from a project graph to see full details
        </div>
      </div>
    );
  }

  // RFQ not found
  if (!rfq) {
    return (
      <div className="text-sm text-muted-foreground">
        RFQ details not available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status */}
      <div>
        <SectionHeader title="STATUS" />
        <StatusBadge status={rfq.state} type="rfq" />
      </div>

      {/* Buyer (Project Customer) */}
      {projectData?.project?.customer && (
        <div>
          <SectionHeader title="BUYER" />
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{projectData.project.customer.name}</span>
          </div>
        </div>
      )}

      {/* Vendor (Direct RFQ vendor) */}
      {rfq.vendor && (
        <div>
          <SectionHeader title="VENDOR" />
          <div className="flex items-center gap-2">
            <Factory className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{rfq.vendor.name}</span>
          </div>
          <VendorContactsList
            contacts={vendorContacts}
            isLoading={contactsLoading}
            maxDisplay={3}
          />
        </div>
      )}

      {/* Semantic ID */}
      <div>
        <SectionHeader title="SEMANTIC ID" />
        <code className="text-xs bg-muted px-2 py-1 rounded block overflow-x-auto">
          {nodeId}
        </code>
      </div>

      {/* Vendor Responses */}
      {rfq.vendor_responses.length > 0 && (
        <div>
          <SectionHeader title="VENDOR RESPONSES" count={rfq.vendor_responses.length} />
          <div className="space-y-2">
            {rfq.vendor_responses.map((vr, idx) => (
              <VendorResponseCard key={vr.cdk || idx} response={vr} index={idx} />
            ))}
          </div>
        </div>
      )}

      {/* RFQ Line Items */}
      <div>
        <SectionHeader title="LINE ITEMS" count={rfq.line_items.length} />
        <LineItemsTable items={rfq.line_items} priceColumn="unit_price" priceLabel="Unit Price" total={rfq.amount_total} />
      </div>

      {/* Open in Odoo */}
      {rfq.odoo_id && (
        <div>
          <SectionHeader title="ACTIONS" />
          <OdooLink
            odooId={rfq.odoo_id}
            model="purchase.order"
            label={rfq.name || `RFQ ${rfq.odoo_id}`}
          />
        </div>
      )}
    </div>
  );
}
